/* global globalThis */
import { loadBoundedExportImage } from './livestreamExport';

export const isRestrictedExportSource = raw => {
  try {
    const u = new URL(raw);
    const prefix = { 'cdn.jambolive.tv': '/media/uploadedphoto/photo/', 's3.ap-northeast-2.amazonaws.com': '/jambolive.tv/media/uploadedphoto/photo/' }[u.hostname];
    return u.protocol === 'https:' && Boolean(prefix) && u.pathname.startsWith(prefix);
  } catch { return false; }
};
const cancelled = () => Object.assign(new Error('已取消匯出'), { name: 'AbortError' });
const check = signal => { if (signal?.aborted) throw cancelled(); };
export const sourceFingerprint = async url => Array.from(new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(url)))).map(x => x.toString(16).padStart(2, '0')).join('');
async function callImages(data) {
  const [{ httpsCallable }, { functions }] = await Promise.all([import('firebase/functions'), import('../firebaseConfig')]);
  return (await httpsCallable(functions, 'getProductExportImages', { timeout: 70000 })(data)).data;
}
/** One export owns this queue; requests are serialized and contain references, never URLs. */
export const createProductExportImageLoader = ({ call = callImages, digest = sourceFingerprint, direct = loadBoundedExportImage } = {}) => {
  let queue = [], scheduled = false, chain = Promise.resolve();
  const flush = () => {
    scheduled = false;
    const jobs = queue.splice(0, 6);
    if (!jobs.length) return;
    chain = chain.then(async () => {
      const live = jobs.filter(j => { if (j.signal?.aborted) { j.reject(cancelled()); return false; } return true; });
      if (!live.length) return;
      try {
        const countries = new Set(live.map(j => j.reference.country));
        if (countries.size !== 1) throw Object.assign(new Error('不可混合國家讀取圖片'), { fatal: true });
        const images = await Promise.all(live.map(async j => ({ productId: j.reference.productId, photo: j.reference.photo, fingerprint: await digest(j.url) })));
        const response = await call({ country: live[0].reference.country, images });
        if (!Array.isArray(response?.images) || response.images.length !== live.length) throw new Error('圖片服務回應不完整');
        live.forEach((job, i) => {
          if (job.signal?.aborted) { job.reject(cancelled()); return; }
          const r = response.images[i];
          if (r.fingerprint !== images[i].fingerprint) { job.reject(Object.assign(new Error('圖片來源驗證不符'), { fatal: true })); return; }
          if (!r.ok) { job.reject(new Error(r.message || '圖片來源讀取失敗')); return; }
          if (r.extension !== 'jpeg' || typeof r.base64 !== 'string' || !/^[A-Za-z0-9+/]+={0,2}$/.test(r.base64) || r.base64.length > 136536) { job.reject(new Error('圖片服務格式不符')); return; }
          job.resolve({ dataUrl: `data:image/jpeg;base64,${r.base64}`, extension: r.extension, byteLength: r.byteLength, width: r.width, height: r.height });
        });
      } catch (error) {
        if (/functions\/(unauthenticated|permission-denied|failed-precondition|not-found|invalid-argument)/.test(error.code || '')) error.fatal = true;
        live.forEach(j => j.reject(error));
      }
    });
    if (queue.length && !scheduled) { scheduled = true; queueMicrotask(flush); }
  };
  return async (url, { signal, reference } = {}) => {
    check(signal);
    if (!isRestrictedExportSource(url)) return direct(url, { signal });
    if (!reference) throw Object.assign(new Error('缺少商品圖片引用，請重新檢查清單'), { fatal: true });
    return new Promise((resolve, reject) => {
      queue.push({ url, reference, signal, resolve, reject });
      if (!scheduled) { scheduled = true; queueMicrotask(flush); }
    });
  };
};
